import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Ticket, Sparkles, Zap, Plus } from "lucide-react"; // CHANGED: icons for the header, tabs and button
import { getNeedHelpTickets } from "../../lib/needHelpApi";
import { markReadFrom } from "../../lib/ticketChats";
import NeedHelpTicketCard from "./NeedHelpTicketCard";
// CHANGED: NeedHelpTicketList (the "View more" screen) is no longer used.
// ADDED: the new ticket details screen.
import NeedHelpTicketDetails from "./NeedHelpTicketDetails";
import styles from "./NeedHelpTickets.module.css";

const ACTIVE_STATUSES = ["Open", "In Progress", "Reopened"];
const CLOSED_STATUSES = ["Resolved", "Closed"];
// REMOVED: PREVIEW_LIMIT (there is no "View more" any more)

const getToken = () => {
  try {
    return localStorage.getItem("token") || "";
  } catch (_e) {
    return "";
  }
};

const newestFirst = (a, b) =>
  new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();

// REMOVED: the old <Section /> component (Active / Resolved stacked sections).

const NeedHelpTickets = ({
  isOpen,
  onClose,
  onCreateTicket,
  userId,
  refreshKey = 0,
  onAttachmentsUploaded,
}) => {
  const [tickets, setTickets] = useState([]);
  // "loading" | "ready" | "error"
  const [status, setStatus] = useState("loading");
  // CHANGED: which tab is selected. "active" (default) | "closed"
  const [tab, setTab] = useState("active");
  // CHANGED: replaces the old `viewMore` state. The ticket whose details
  // screen is open (null = list screen).
  const [selectedId, setSelectedId] = useState(null);
  // ADDED: the ticket card the user clicked (shown highlighted). It stays
  // highlighted when the user comes back from the details screen.
  const [highlightedId, setHighlightedId] = useState(null);
  // ADDED: true when the details screen was opened with the chat icon. It
  // then scrolls down to the chat. Opened with "View" it stays at the top.
  const [scrollToChat, setScrollToChat] = useState(false);

  const loadTickets = useCallback(
    async ({ silent = false } = {}) => {
      // A silent refresh keeps the list on screen: no spinner, no empty list.
      if (!silent) setStatus("loading");
      try {
        const list = await getNeedHelpTickets({ userId, token: getToken() });
        
        // Only tickets raised from the Need Help modal
        // (createNeedHelpTicket saves them with category "Need Help").
        // setTickets(list.filter((t) => t.category === "Need Help"));

        setTickets(list);
        setStatus("ready");
      } catch (_e) {
        // A failed silent refresh must not replace a list that is showing.
        if (!silent) setStatus("error");
      }
    },
    [userId],
  );

  // Hit the API every time the screen opens.
  useEffect(() => {
    if (!isOpen) return;
    setSelectedId(null); // CHANGED: was setViewMore(null)
    setTab("active"); // ADDED: Active is always selected when the screen opens
    setHighlightedId(null); // ADDED
    setScrollToChat(false); // ADDED
    setTickets([]);
    loadTickets();
  }, [isOpen, loadTickets]);

  // A ticket was just created (the page bumps refreshKey): reload quietly so
  // the new ticket appears in Active Tickets without a spinner.
  useEffect(() => {
    if (!refreshKey || !isOpen) return;
    loadTickets({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  // The user opened a ticket's chat: the backend marked the admin's messages
  // as read, so mirror that here (this removes the dot on the card).
  const markChatRead = useCallback((ticketId) => {
    setTickets((prev) =>
      prev.map((t) =>
        t.ticketId === ticketId
          ? { ...t, chats: markReadFrom(t.chats, "admin") }
          : t,
      ),
    );
  }, []);

  // The details screen added / deleted attachments: mirror that in the list.
  const updateTicketAttachments = useCallback((ticketId, attachments) => {
    setTickets((prev) =>
      prev.map((t) => (t.ticketId === ticketId ? { ...t, attachments } : t)),
    );
  }, []);

  const { activeTickets, closedTickets } = useMemo(
    () => ({
      activeTickets: tickets
        .filter((t) => ACTIVE_STATUSES.includes(t.status))
        .sort(newestFirst),
      closedTickets: tickets
        .filter((t) => CLOSED_STATUSES.includes(t.status))
        .sort(newestFirst),
    }),
    [tickets],
  );

  // ADDED: switching tab clears the highlighted ticket.
  const selectTab = (next) => {
    setTab(next);
    setHighlightedId(null);
  };

  // ADDED: the ticket of the open details screen, always read from the list
  // so it stays up to date.
  const selectedTicket = useMemo(
    () => tickets.find((t) => t.ticketId === selectedId) || null,
    [tickets, selectedId],
  );

  if (!isOpen) return null;

  if (selectedTicket) {
    return (
      <NeedHelpTicketDetails
        ticket={selectedTicket}
        onBack={() => {
          setSelectedId(null);
          setScrollToChat(false);
        }}
        onChatRead={markChatRead}
        scrollToBottom={scrollToChat}
        onAttachmentsChange={updateTicketAttachments}
        onAttachmentsUploaded={onAttachmentsUploaded}
      />
    );
  }

  const hasTickets = activeTickets.length + closedTickets.length > 0;

  const renderBody = () => {
    if (status === "loading") {
      return (
        <div className={styles.center}>
          <span className={styles.spinner} aria-label="Loading tickets" />
        </div>
      );
    }

    if (status === "error") {
      return (
        <div className={styles.center}>
          <p className={styles.emptyText}>
            We couldn't load your tickets. Check your connection and try again.
          </p>
          <button
            type="button"
            className={styles.centerBtn}
            onClick={loadTickets}
          >
            Try again
          </button>
        </div>
      );
    }

    // No tickets at all: the original centre content.
    if (!hasTickets) {
      return (
        <div className={styles.center}>
          <div className={styles.emptyIcon}>
            <Ticket size={30} strokeWidth={1.8} aria-hidden="true" />
          </div>
          <h3 className={styles.emptyTitle}>Need a hand from our team?</h3>
          <p className={styles.emptyText}>
            Create a support ticket and we'll get back to you as soon as we can.
          </p>
          <button
            type="button"
            className={styles.centerBtn}
            onClick={onCreateTicket}
          >
            Create a ticket
          </button>
        </div>
      );
    }

    // CHANGED: two badges (tabs) + one list for the selected badge.
    const visibleTickets = tab === "active" ? activeTickets : closedTickets;

    return (
      <div className={styles.sections}>
        {/* CHANGED: tabs now have an icon, the label and the count */}
        <div className={styles.tabs} role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "active"}
            className={`${styles.tab} ${tab === "active" ? styles.tabActive : ""}`}
            onClick={() => selectTab("active")}
          >
            <span className={styles.tabIcon} aria-hidden="true">
              <Zap size={16} strokeWidth={2.2} />
            </span>
            <span className={styles.tabLabel}>Active</span>
            <span className={styles.tabCount}>{activeTickets.length}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "closed"}
            className={`${styles.tab} ${tab === "closed" ? styles.tabActive : ""}`}
            onClick={() => selectTab("closed")}
          >
            <span className={styles.tabIcon} aria-hidden="true">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="m9 12 2 2 4-4" />
              </svg>
            </span>
            <span className={styles.tabLabel}>Resolved</span>
            <span className={styles.tabCount}>{closedTickets.length}</span>
          </button>
        </div>

        {visibleTickets.length === 0 ? (
          <p className={styles.listEmpty}>
            {tab === "active"
              ? "No active tickets right now."
              : "No resolved tickets yet."}
          </p>
        ) : (
          <div className={styles.listScroll}>
            {visibleTickets.map((ticket) => (
              <NeedHelpTicketCard
                key={ticket.ticketId}
                ticket={ticket}
                selected={ticket.ticketId === highlightedId} // ADDED
                onSelect={setHighlightedId} // ADDED: the card was clicked
                onView={(id) => {
                  // CHANGED: "View" also highlights the card, so it is still
                  // marked when the user comes back from the details screen.
                  setHighlightedId(id);
                  setScrollToChat(false); // ADDED: normal open, no scrolling
                  setSelectedId(id);
                }}
                onOpenChat={(id) => {
                  // ADDED: the chat icon opens the details screen and scrolls
                  // down to the chat.
                  setHighlightedId(id);
                  setScrollToChat(true);
                  setSelectedId(id);
                }}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.screen} role="dialog" aria-label="Tickets">
      <header className={styles.headBar}>
        <button
          type="button"
          className={styles.backBtn}
          onClick={onClose}
          aria-label="Back"
        >
          <svg
            width="9"
            height="15"
            viewBox="21 18.34 7.66 13.32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M21.293 25.7071C20.9025 25.3166 20.9025 24.6834 21.293 24.2929L26.9499 18.636C27.3404 18.2455 27.9736 18.2455 28.3641 18.636C28.7546 19.0266 28.7546 19.6597 28.3641 20.0503L23.4144 25L28.3641 29.9497C28.7546 30.3403 28.7546 30.9734 28.3641 31.364C27.9736 31.7545 27.3404 31.7545 26.9499 31.364L21.293 25.7071Z"
              fill="#0A0A0A"
              fillOpacity="0.9"
            />
          </svg>
        </button>
        {/* CHANGED: title + subtitle on the left, sparkle icon on the right */}
        <div className={styles.headText}>
          <h2 className={styles.headTitle}>Tickets</h2>
          <p className={styles.headSub}>
            Manage and track your support tickets
          </p>
        </div>
        <span className={styles.headSpark} aria-hidden="true">
          <Sparkles size={18} strokeWidth={2} />
        </span>
      </header>

      <div className={`${styles.body} ${styles.bodyFixed}`}>{renderBody()}</div>

      <div className={styles.footer}>
        <button
          type="button"
          className={styles.footerBtn}
          onClick={onCreateTicket}
        >
          <Plus size={18} strokeWidth={2.4} aria-hidden="true" /> {/* ADDED */}
          Create Ticket
        </button>
      </div>
    </div>
  );
};

export default NeedHelpTickets;
